import React from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatPercent, formatPosition, scoreBand } from '../format';

const BAND_ICONS = { critical: AlertOctagon, warning: AlertTriangle, good: CheckCircle2 };

const StatTile = ({ label, value, detail, valueClassName = 'text-4xl' }) => (
  <div className="rp-keep border-t border-[var(--lp-line)] pt-5">
    <p className="text-sm text-[var(--lp-muted)]">{label}</p>
    <p className={`lp-display mt-2 leading-none ${valueClassName}`}>{value}</p>
    <p className="mt-2 text-sm text-[var(--lp-muted)]">{detail}</p>
  </div>
);

const ScoreHero = ({ report }) => {
  const { summary, competitors, methodology } = report;
  const band = scoreBand(summary.visibilityScore);
  const BandIcon = BAND_ICONS[band.key];
  const leader = competitors[0];

  return (
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
      <div className="rp-keep lg:col-span-5">
        <p className="lp-kicker">Puntuación de visibilidad en IA</p>
        <p className="mt-4 flex items-baseline gap-3">
          <span className="rp-score lp-display text-[clamp(6rem,14vw,10rem)] leading-[0.8]">{summary.visibilityScore}</span>
          <span className="lp-display text-3xl text-[var(--lp-muted)]">/100</span>
        </p>

        <div
          className={`rp-meter-track--${band.key} mt-8 h-3 w-full overflow-hidden rounded-full`}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={summary.visibilityScore}
          aria-label="Puntuación de visibilidad en IA"
        >
          <div
            className={`rp-meter-fill--${band.key} h-full rounded-full`}
            style={{ width: `${Math.max(summary.visibilityScore, 2)}%` }}
          />
        </div>
        <p className="mt-4 flex items-center gap-2 font-semibold">
          <BandIcon size={18} className={`rp-status-${band.key}`} aria-hidden="true" />
          {band.label}
        </p>
      </div>

      <div className="grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:col-span-7">
        <StatTile
          label="Apareces en"
          value={`${summary.appearances} de ${summary.answersAnalyzed}`}
          detail={`respuestas de la IA (${formatPercent(summary.appearances, summary.answersAnalyzed)})`}
        />
        <StatTile
          label="Tu mejor puesto"
          value={formatPosition(summary.bestPosition)}
          detail={summary.bestPosition ? 'en alguna de las respuestas' : 'no apareces en ninguna respuesta'}
        />
        {leader && (
          <StatTile
            label="Quien más aparece"
            value={leader.name}
            valueClassName="text-2xl leading-tight"
            detail={`en el ${formatPercent(leader.appearances, summary.answersAnalyzed)} de las respuestas`}
          />
        )}
        <StatTile
          label="Preguntas analizadas"
          value={summary.questionsAsked}
          detail={
            methodology.samplesPerQuestion > 1
              ? `cada una hecha ${methodology.samplesPerQuestion} veces`
              : 'distintas, como las haría un cliente'
          }
        />
      </div>
    </div>
  );
};

export default ScoreHero;
